// /src/auth/AuthContext.jsx
import React, {
  createContext, useContext, useEffect, useMemo, useRef, useState, useCallback,
} from 'react';
import api from '../utils/axios.secure';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// 다양한 서버 응답 키를 role로 정규화
function normalizeRole(obj) {
  const r =
    obj?.role ??
    obj?.authority ??
    (Array.isArray(obj?.roles) ? obj.roles[0] : obj?.roles) ??
    obj?.auth ??
    null;
  return r ? String(r).toUpperCase() : null;
}

// /me 응답에서 실제 사용자 오브젝트만 추출
function unwrapMeResponse(data) {
  if (data?.user) return data.user;                 // { ok, user:{...} }
  if (data?.data && typeof data.data === 'object') return data.data; // { status, data:{...} }
  return data;
}

export default function AuthProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(null);
  const [remainingTime, setRemainingTime] = useState(null);
  const timerRef = useRef(null);

  // ⏱ 타이머 정리
  const clearTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  // exp(초) 기반 카운트다운 시작
  const startTimer = useCallback((expSec) => {
    clearTimer();
    const exp = Number(expSec);
    if (!exp || Number.isNaN(exp)) {
      setRemainingTime(null);
      return;
    }
    const tick = () => {
      const now = Math.floor(Date.now() / 1000);
      const left = exp - now;
      if (left <= 0) {
        // 만료 시 서버 로그아웃 시도 후 정리
        try { api.post('/manager/user/logout', {}, { withCredentials: true }); } catch { }
        setUser(null);
        setRemainingTime(0);
        clearTimer();
        return;
      }
      setRemainingTime(left);
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
  }, [clearTimer]);

  // /me에서 user + exp 확보 → 타이머 시작
  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get('/manager/user/me', { withCredentials: true });
      const raw = unwrapMeResponse(data) ?? null;

      if (!raw) {
        setUser(null);
        setRemainingTime(null);
        clearTimer();
        return;
      }

      // id/role/name 정규화
      const role = normalizeRole(raw);
      const id = raw?.id ?? raw?.sub ?? raw?.admin_id ?? raw?.manager_id ?? null;
      const name = raw?.name ?? id;
      const exp = typeof raw?.exp === 'number' ? raw.exp : null;

      setUser({ ...raw, id, name, role, exp });
      if (exp) startTimer(exp);
      else setRemainingTime(null);
    } catch {
      setUser(null);
      setRemainingTime(null);
      clearTimer();
    } finally {
      setReady(true);
    }
  }, [startTimer, clearTimer]);

  // 최초 1회 부트스트랩
  useEffect(() => {
    bootstrap();
    return () => clearTimer();
  }, [bootstrap]);

  const login = useCallback(async (credentials) => {
    let resp;
    try {
      resp = await api.post('/manager/user/login', credentials, {
        withCredentials: true,
      });
    } catch (err) {
      console.error('[login failed]', {
        status: err?.response?.status,
        data: err?.response?.data,
        headers: err?.response?.headers,
      });
      throw err;
    }

    // 로그인 후 /me 재확인 + 타이머 시작
    await bootstrap();
    
    return resp?.data?.data?.data || null;
  }, [bootstrap]);

  const logout = useCallback(async () => {
    try { await api.post('/manager/user/logout', {}, { withCredentials: true }); } catch { }
    setUser(null);
    setRemainingTime(null);
    clearTimer();
  }, [clearTimer]);

  const fmtTime = useCallback((sec) => {
    if (sec == null) return '—';
    const s = Math.max(0, Math.floor(sec));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    return h ? `${h}시간 ${m}분 ${r < 10 ? '0' : ''}${r}초` : `${m}분 ${r < 10 ? '0' : ''}${r}초`;
  }, []);

  const value = useMemo(() => ({
    ready, user, login, logout, remainingTime, fmtTime,
  }), [ready, user, login, logout, remainingTime, fmtTime]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
